import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Lock, Sparkles, ArrowRight, Eye } from "lucide-react";
import { ArticleInteractions } from "@/components/articles/ArticleInteractions";
import { useSupabase } from "@/hooks/useSupabase";
import { useToast } from "@/hooks/use-toast";
import type { Article } from "@/types/article";

interface ArticleCardProps {
  article: Article;
  hasPremiumAccess: boolean;
  onPremiumAccess: (isPremium: boolean) => boolean;
}

const ArticleCard: React.FC<ArticleCardProps> = ({ article, hasPremiumAccess, onPremiumAccess }) => {
  const navigate = useNavigate();
  const { user, isLoggedIn } = useSupabase();
  const { toast } = useToast();

  // --- More/less for description preview ---
  const [showFull, setShowFull] = useState(false);
  const [isOverflowed, setIsOverflowed] = useState(false);
  const descRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (descRef.current) {
      // Roughly check for overflow by comparing scrollHeight and clientHeight
      setIsOverflowed(descRef.current.scrollHeight > descRef.current.clientHeight + 6);
    }
  }, [article.description, showFull]);

  const handleArticleClick = () => {
    // Check for premium access
    if (article.premium && !isLoggedIn) {
      toast({
        title: "Premium Content",
        description: "Please log in to read premium articles",
        variant: "destructive",
      });
      navigate('/login', { state: { returnTo: `/article/${article.id}` } });
      return;
    }
    
    // Use the string version of the ID for navigation
    const articleId = typeof article.id === 'number' ? article.id : article.id;
    navigate(`/article/${articleId}`);
  };

  const getButtonText = () => {
    if (article.premium && !isLoggedIn) {
      return { 
        text: 'Login to Read', 
        icon: <Lock className="ml-2 h-4 w-4" />, 
        action: () => {
          toast({
            title: "Premium Content",
            description: "Please log in to read premium articles",
            variant: "destructive",
          });
          navigate('/login', { state: { returnTo: `/article/${article.id}` } });
        }
      };
    }
    return { 
      text: 'Read Article', 
      icon: <ArrowRight className="ml-2 h-4 w-4" />, 
      action: handleArticleClick 
    };
  };

  const buttonConfig = getButtonText();

  return (
    <Card
      className={`overflow-hidden hover:shadow-md transition-shadow duration-300 flex flex-col h-full ${article.premium && !isLoggedIn ? 'opacity-75' : ''} cursor-pointer`}
      style={{ minWidth: '350px', maxWidth: '100%', maxHeight: "580px" }}
      onClick={handleArticleClick}
    >
      <div className="h-48 overflow-hidden relative">
        <img 
          src={article.image} 
          alt={article.title} 
          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.onerror = null;
            target.src = "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b";
          }}
        />
        {article.premium && !isLoggedIn && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Lock className="h-8 w-8 text-white/90" />
          </div>
        )}
      </div>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <Badge variant="outline" className="text-xs font-medium text-gray-500 border-gray-200">
            {article.category}
          </Badge>
          {article.premium && (
            <Badge className="bg-premium text-premium-foreground">
              <Sparkles className="h-3 w-3 mr-1" /> Premium
            </Badge>
          )}
        </div>
        <CardTitle className="text-xl mt-2 hover:text-ai-indigo">
          {article.title}
        </CardTitle>
        <CardDescription className="text-gray-500 text-sm">
          {article.readTime} • {article.date}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-grow pb-0">
        <p
          ref={descRef}
          className={
            `text-gray-600 transition-all duration-150 ease-in-out ${showFull ? '' : 'line-clamp-4'}`
          }
          style={{ minHeight: 0, marginBottom: '0.5rem' }}
        >
          {article.description}
        </p>
        {isOverflowed && (
          <button
            type="button"
            className="text-ai-indigo underline text-xs font-medium hover:text-ai-purple transition-colors"
            onClick={(e) => {
              e.stopPropagation(); // Prevent card click
              setShowFull(v => !v);
            }}
            tabIndex={0}
          >
            {showFull ? "Show less" : "More"}
          </button>
        )}
      </CardContent>
      <CardFooter className="flex flex-col space-y-4 pt-2 pb-6">
        <Button 
          variant="ghost" 
          className="text-ai-indigo hover:text-ai-purple hover:bg-ai-indigo/10 w-full justify-center"
          onClick={(e) => {
            e.stopPropagation(); // Prevent default card click
            buttonConfig.action();
          }}
          disabled={false}
        >
          {buttonConfig.text} {buttonConfig.icon}
        </Button>
        <div className="flex w-full justify-between items-center text-xs text-gray-500 px-1" onClick={(e) => e.stopPropagation()}>
          <span className="flex items-center gap-1">
            <Eye className="h-4 w-4 mr-1" />
            {article.views} views
          </span>
          <div className="flex gap-2">
            <ArticleInteractions articleId={article.id.toString()} />
          </div>
        </div>
      </CardFooter>
    </Card>
  );
};

export default ArticleCard;
